import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IGenderDTO } from '@common-frontend-submodule/interfaces/gender/igender.interface';
import { IGenderCreationDTO } from '@common-frontend-submodule/interfaces/gender/igender-creation.interface';
import { IGenderUpdateDTO } from '@common-frontend-submodule/interfaces/gender/igender-update.interface';
import { IGetGendersRequestDTO } from '@common-frontend-submodule/interfaces/gender/iget-genders-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { GenderRoutes } from '@common-frontend-submodule/enums/routes/gender-routes.enum';

const apiClient = ApiClient.getInstance()

interface GenderContextProps {
  getById: (genderId: string) => Promise<IApiResponseGenericDTO<IGenderDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IGenderDTO[]>>;
  filter: (model: IGetGendersRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IGenderDTO>>>;
  create: (model: IGenderCreationDTO) => Promise<IApiResponseGenericDTO<IGenderDTO>>;
  update: (genderId: string, model: IGenderUpdateDTO) => Promise<IApiResponseGenericDTO<IGenderDTO>>;
  delete: (genderId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const GenderContext = createContext<GenderContextProps | undefined>(undefined);

export const GenderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (genderId: string): Promise<IApiResponseGenericDTO<IGenderDTO>> => {
    return await apiClient.getRequest<IGenderDTO>(`${GenderRoutes.BasePrefix}/${genderId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IGenderDTO[]>> => {
    return await apiClient.getRequest<IGenderDTO[]>(`${GenderRoutes.BasePrefix}/${GenderRoutes.All}`);
  };

  const filter = async (model: IGetGendersRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IGenderDTO>>> => {
    return await apiClient.postRequest<IGetGendersRequestDTO, IPaginatedResultDTO<IGenderDTO>>(
      `${GenderRoutes.BasePrefix}/${GenderRoutes.List}`,
      model
    );
  };

  const create = async (model: IGenderCreationDTO): Promise<IApiResponseGenericDTO<IGenderDTO>> => {
    return await apiClient.postRequest<IGenderCreationDTO, IGenderDTO>(GenderRoutes.BasePrefix, model);
  };

  const update = async (genderId: string, model: IGenderUpdateDTO): Promise<IApiResponseGenericDTO<IGenderDTO>> => {
    return await apiClient.putRequest<IGenderUpdateDTO, IGenderDTO>(`${GenderRoutes.BasePrefix}/${genderId}`, model);
  };

  const deleteGender = async (genderId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${GenderRoutes.BasePrefix}/${genderId}`);
  };

  return (
    <GenderContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteGender }}>
      {children}
    </GenderContext.Provider>
  );
};

export const useGenderStore = () => {
  const context = useContext(GenderContext);
  if (!context) {
    throw new Error('useGenderStore must be used within a GenderProvider');
  }
  return context;
};
